import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { OptionReminder } from '@/components/ui/option-reminder';
import { useToast } from '@/hooks/use-toast';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import { DatasourceInstructionMode } from '@/lib/graphql/generated/graphql';
import { useAddWebsiteDataSource } from '@/lib/hooks/useAddWebsiteDataSource';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import { dealAtom } from '@/states/deal.state';
import { useAtomValue } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import { BaseImportViewProps } from './import-view-props';

interface ViewProductsScrapingProps extends BaseImportViewProps {
  dealId: string;
  instructionMode?: DatasourceInstructionMode;
  onModifyOption?: () => void;
  onCloseDialog?: () => void;
  cameFromOptions?: boolean;
}

export const ViewProductsScraping: React.FC<ViewProductsScrapingProps> = ({
  setImportView,
  dealId,
  instructionMode,
  onModifyOption,
  onCloseDialog,
  cameFromOptions = false,
}) => {
  const { t } = useTranslation();
  const [urlInput, setUrlInput] = useState('');

  // Hooks
  const deal = useAtomValue(dealAtom);
  const {
    addWebsiteDataSource,
    loading: loadingUrl,
    error: errorUrl,
    domainError,
    isDomainError: isDomainErrorUrl,
  } = useAddWebsiteDataSource();
  const { toast } = useToast();
  const progressToastTrackerIdRef = useRef<string | null>(null);

  const clearProgressToast = () => {
    if (!progressToastTrackerIdRef.current) return;
    clearDealItemsInProgressToast({
      trackerId: progressToastTrackerIdRef.current,
    });
    progressToastTrackerIdRef.current = null;
  };

  useErrorToastHandler({
    domainError,
    isDomainError: isDomainErrorUrl,
    error: errorUrl ?? null,
    domainToast: {
      titleKey: 'general.error_scraping',
      defaultMessageKey: 'general.error_scraping_message',
      clearProgress: clearProgressToast,
    },
    genericToast: {
      titleKey: 'general.error_scraping',
      getDescription: (err) =>
        (err as Error)?.message === 'unsupportedCms'
          ? t('general.error_scraping_unsupported_cms')
          : t('general.error_scraping_message'),
    },
  });

  const handleSubmit = async () => {
    try {
      new URL(urlInput);
    } catch {
      alert(t('general.error_valid_url'));
      return;
    }
    if (!deal?.hubspotDealId) {
      alert(t('general.error_hubspot_id_invalid'));
      return;
    }
    const { update, dismiss } = toast({
      description: `⏳ ${t('general.scraping_in_progress')}`,
      duration: Number.POSITIVE_INFINITY,
    });
    const toastTrackerId = registerDealItemsInProgressToast(
      update as ToastUpdate,
      dismiss
    );
    progressToastTrackerIdRef.current = toastTrackerId;
    try {
      const result = await addWebsiteDataSource({
        dealId,
        hubspotDealId: deal.hubspotDealId,
        websiteUrl: urlInput,
        productUrl: '',
        instructionMode,
      });

      if (result.success) {
        bindDealItemsInProgressToastToDatasource(toastTrackerId, result.id);
        progressToastTrackerIdRef.current = null;
        onCloseDialog?.();
      } else {
        clearProgressToast();
      }
    } catch {
      clearProgressToast();
    }
  };

  return (
    <div className="flex h-[600px] flex-col">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() =>
              setImportView(
                cameFromOptions ? 'productsScrapingOptionSelection' : 'products'
              )
            }
            aria-label={
              cameFromOptions ? 'Revenir aux options' : 'Revenir au menu'
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {t('components.import_flow.scraping.title')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.scraping.description')}
        </DialogDescription>
      </DialogHeader>

      <div className="flex-1 space-y-4 overflow-auto p-4">
        {instructionMode && onModifyOption && (
          <OptionReminder
            instructionMode={instructionMode}
            onModify={onModifyOption}
          />
        )}
        <div className="flex flex-col space-y-2">
          <label className="block text-sm font-medium">
            {t('general.url_website')} *
          </label>
          <Input
            className="h-10"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://..."
            disabled={loadingUrl}
          />
        </div>
      </div>

      {/* Footer with action buttons */}
      <div className="flex justify-end space-x-4 p-4">
        <Button
          variant="outline"
          onClick={() =>
            setImportView(
              cameFromOptions ? 'productsScrapingOptionSelection' : 'products'
            )
          }
        >
          {t('general.previous')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loadingUrl || !urlInput.trim()}
        >
          {loadingUrl
            ? t('general.importation_in_progress')
            : t('general.importation_confirm')}
        </Button>
      </div>
    </div>
  );
};
